// ProjectList.js
import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  Paper,
  Stack,
  Snackbar,
  Alert,
  useTheme,
  Button,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Visibility,
  DragIndicator,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Link } from "react-router-dom";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";
import {
  PROJECT_STATUS,
  PROJECT_STATUS_LABELS,
  PROGRESS_STEPS,
  STATUS_ORDER,
} from "../../utils/models/projectModel";

const ITEMS_PER_PAGE = 3;

const ProjectList = ({ onView, onEdit, filter = {} }) => {
  const theme = useTheme();
  const { data: projects, loading } = useCollection("projects");
  const { remove, update: updateDoc } = useAuditFirestore("projects");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [localProjects, setLocalProjects] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});

  React.useEffect(() => {
    if (projects) {
      setLocalProjects(projects);
    }
  }, [projects]);

  const filtered = localProjects?.filter((p) => {
    if (p.isActive === false) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        p.name?.toLowerCase().includes(searchLower) ||
        p.clientName?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const grouped = useMemo(() => {
    const groups = {};
    STATUS_ORDER.forEach((status) => {
      groups[status] =
        filtered
          ?.filter((p) => p.status === status)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) || [];
    });
    return groups;
  }, [filtered]);

  const getVisibleItems = (status, items) => {
    const max = visibleCounts[status] || ITEMS_PER_PAGE;
    return items.slice(0, max);
  };

  const hasMore = (status, items) => {
    const max = visibleCounts[status] || ITEMS_PER_PAGE;
    return items.length > max;
  };

  const canShowLess = (status) => {
    return (visibleCounts[status] || ITEMS_PER_PAGE) > ITEMS_PER_PAGE;
  };

  const showMore = (status) => {
    setVisibleCounts(prev => ({
      ...prev,
      [status]: (prev[status] || ITEMS_PER_PAGE) + ITEMS_PER_PAGE
    }));
  };

  const showLess = (status) => {
    setVisibleCounts(prev => ({
      ...prev,
      [status]: Math.max(ITEMS_PER_PAGE, (prev[status] || ITEMS_PER_PAGE) - ITEMS_PER_PAGE)
    }));
  };

  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result;
      if (!destination) return;
      if (source.droppableId === destination.droppableId && source.index === destination.index) return;

      const newStatus = destination.droppableId;
      const oldStatus = source.droppableId;

      setLocalProjects((prev) =>
        prev.map((project) =>
          project.id === draggableId
            ? { ...project, status: newStatus, updatedAt: new Date().toISOString() }
            : project,
        ),
      );

      try {
        await updateDoc(draggableId, { status: newStatus, updatedAt: new Date().toISOString() });
      } catch (error) {
        console.error("Error updating status:", error);
        setLocalProjects((prev) =>
          prev.map((project) =>
            project.id === draggableId ? { ...project, status: oldStatus } : project,
          ),
        );
        setErrorMessage("Error al actualizar el estado del proyecto");
      }
    },
    [updateDoc],
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setLocalProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
    const deletedProject = deleteTarget;
    setDeleteTarget(null);
    try {
      await remove(deletedProject.id);
    } catch (error) {
      console.error("Error deleting:", error);
      setLocalProjects((prev) => [...prev, deletedProject]);
      setErrorMessage("Error al eliminar el proyecto");
    }
  };

  if (loading || !localProjects) return <LoadingSpinner />;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
          background: theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <TextField
            placeholder="Buscar por nombre o cliente"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: "260px" },
              "& .MuiOutlinedInput-root": { borderRadius: "12px", background: theme.palette.background.paper, height: 44 },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: theme.palette.primary.main }} /></InputAdornment>,
              endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch("")}>✖</IconButton></InputAdornment>,
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", height: 44, gap: 1 }}>
            <Chip label={`Total: ${filtered?.length || 0}`} sx={{ height: 44, borderRadius: "12px", fontWeight: 700, fontSize: { xs: "0.75rem", sm: "0.85rem" }, background: theme.palette.background.paper, border: "1px solid", borderColor: theme.palette.primary.main, color: theme.palette.primary.main }} />
            <Tooltip title="Refrescar">
              <IconButton onClick={() => { if (projects) setLocalProjects(projects); }} size="small" sx={{ color: theme.palette.primary.main }}><Refresh /></IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {filtered?.length === 0 ? (
        <EmptyState title={search ? "Sin resultados" : "Sin proyectos"} description={search ? "Prueba con otros términos" : "Añade tu primer proyecto"} actionLabel="Nuevo Proyecto" onAction={() => onEdit(null)} />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: { xs: 1.5, sm: 2, md: 2.5 }, width: "100%" }}>
            {Object.entries(grouped).map(([status, items]) => {
              const visibleItems = getVisibleItems(status, items);
              const showMoreButton = hasMore(status, items);
              const showLessButton = canShowLess(status);
              const remaining = items.length - visibleItems.length;
              const showing = visibleItems.length;
              const total = items.length;
              
              return (
                <Box key={status} sx={{ minWidth: 0 }}>
                  <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, background: theme.palette.mode === 'dark' ? 'rgba(0, 95, 101, 0.08)' : 'rgba(0, 250, 210, 0.05)', minHeight: 200, height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" noWrap sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>{PROJECT_STATUS_LABELS[status]}</Typography>
                      <Chip label={items.length} size="small" sx={{ fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }} />
                    </Box>

                    <Droppable droppableId={status}>
                      {(provided, snapshot) => (
                        <Stack ref={provided.innerRef} {...provided.droppableProps} spacing={1.5} sx={{ flex: 1, minHeight: 80, background: snapshot.isDraggingOver ? "rgba(0, 225, 210, 0.1)" : "transparent", borderRadius: "12px", border: snapshot.isDraggingOver ? "2px dashed #00e1d2" : "2px dashed transparent", transition: "all 0.2s", p: 1 }}>
                          {visibleItems.map((project, index) => (
                            <Draggable key={project.id} draggableId={project.id} index={index}>
                              {(provided, snapshot) => (
                                <Card ref={provided.innerRef} {...provided.draggableProps} elevation={0} sx={{ borderRadius: "12px", border: "1px solid", borderColor: snapshot.isDragging ? theme.palette.primary.main : theme.palette.divider, background: snapshot.isDragging ? "rgba(0, 250, 210, 0.2)" : theme.palette.mode === 'dark' ? "rgba(0, 175, 163, 0.2)" : "rgba(0, 250, 210, 0.15)", cursor: snapshot.isDragging ? "grabbing" : "default", "&:hover": { boxShadow: "0 4px 16px rgba(0, 175, 163, 0.15)", transform: snapshot.isDragging ? "none" : "translateY(-2px)" } }}>
                                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                                      <Box {...provided.dragHandleProps} sx={{ mr: 1, mt: 0.5, color: "text.disabled", cursor: "grab", display: "flex" }}><DragIndicator sx={{ fontSize: { xs: 18, sm: 20 } }} /></Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ cursor: "pointer", mb: 0.5, wordBreak: "break-word", fontSize: { xs: "0.75rem", sm: "0.85rem" } }} onClick={() => onView(project)}>{project.name || "Sin nombre"}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: "break-word", fontSize: { xs: "0.6rem", sm: "0.7rem" } }}>{project.address?.street ? `${project.address.street}, ${project.address.city || ""} ${project.address.province || ""}` : "Sin dirección"}</Typography>
                                        <Box sx={{ mb: 1 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 500, wordBreak: "break-word", fontSize: { xs: "0.6rem", sm: "0.7rem" } }}>{project.clientName || "Sin cliente"}</Typography>
                                        </Box>
                                        {project.status === PROJECT_STATUS.COMPLETED && project.totalInvoiced > 0 && (
                                          <Typography variant="caption" fontWeight={700} color="success.main" sx={{ display: "block", mb: 1, fontSize: { xs: "0.6rem", sm: "0.7rem" } }}>{project.totalInvoiced.toLocaleString('es-ES')}€</Typography>
                                        )}
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.3, mb: 1, px: 0.5 }}>
                                          {PROGRESS_STEPS.map((step, idx) => {
                                            const completed = project[step.key];
                                            return (
                                              <React.Fragment key={step.key}>
                                                <Tooltip title={step.label} arrow>
                                                  <Box sx={{ width: { xs: 18, sm: 22 }, height: { xs: 18, sm: 22 }, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: completed ? theme.palette.success.main : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', color: completed ? "white" : "text.disabled", fontSize: { xs: 9, sm: 11 }, fontWeight: 700 }}>{completed ? "✓" : step.icon}</Box>
                                                </Tooltip>
                                                {idx < PROGRESS_STEPS.length - 1 && (
                                                  <Box sx={{ flex: 1, height: 2, bgcolor: completed ? theme.palette.success.main : theme.palette.divider }} />
                                                )}
                                              </React.Fragment>
                                            );
                                          })}
                                        </Box>
                                        {project.contractId && (
                                          <Link to={`/contracts/${project.contractId}`} style={{ textDecoration: "none" }}>
                                            <Typography variant="caption" color="primary" sx={{ display: "block", mb: 1, fontSize: { xs: "0.6rem", sm: "0.7rem" } }} noWrap>📋 Ver contrato</Typography>
                                          </Link>
                                        )}
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", ml: 4, gap: 1 }}>
                                      <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => onView(project)} sx={{ color: theme.palette.info.main }}><Visibility sx={{ fontSize: { xs: 18, sm: 20 } }} /></IconButton></Tooltip>
                                        <Tooltip title="Editar"><IconButton size="small" onClick={() => onEdit(project)} sx={{ color: theme.palette.primary.main }}><Edit sx={{ fontSize: { xs: 18, sm: 20 } }} /></IconButton></Tooltip>
                                        <Tooltip title="Eliminar"><IconButton size="small" onClick={() => setDeleteTarget(project)} sx={{ color: theme.palette.error.main }}><Delete sx={{ fontSize: { xs: 18, sm: 20 } }} /></IconButton></Tooltip>
                                      </Stack>
                                    </Box>
                                  </CardContent>
                                </Card>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Stack>
                      )}
                    </Droppable>

                    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {showMoreButton && (
                        <Button fullWidth size="small" onClick={() => showMore(status)} endIcon={<ExpandMore />} variant="outlined"
                          sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" }, color: theme.palette.primary.main, borderColor: theme.palette.primary.main }}>
                          Ver {Math.min(remaining, ITEMS_PER_PAGE)} más ({showing}/{total})
                        </Button>
                      )}
                      {showLessButton && (
                        <Button fullWidth size="small" onClick={() => showLess(status)} endIcon={<ExpandLess />} variant="outlined"
                          sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" }, color: theme.palette.text.secondary, borderColor: theme.palette.divider }}>
                          Ver menos
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Box>
              );
            })}
          </Box>
        </DragDropContext>
      )}

      <Snackbar open={!!errorMessage} autoHideDuration={4000} onClose={() => setErrorMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}><Alert severity="error">{errorMessage}</Alert></Snackbar>
      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} title="Eliminar Proyecto" message={`¿Eliminar el proyecto "${deleteTarget?.name}"?`} type="delete" />
    </Box>
  );
};

export default ProjectList;